import { z } from "zod";

import { DynamicValue, DynamicValueTypes } from "./DynamicValue.js";
import { zGuard } from "./zod/utils.js";
import { NumberArray, StringArray } from "./parameterValueType.js";
import { getNormalEnum } from "./pseudoEnums.js";

export const ParameterInputTypeLiteral = z.enum([
  "TEXT_INPUT",
  "NUMERIC_INPUT",
  "DROPDOWN",
  "TABLE",
  "SLIDER",
  "DATE",
  "BUTTON",
  "CHECKBOX",
  "MULTISELECT",
  "FILE_UPLOAD",
]);
export type ParameterInputType = z.infer<typeof ParameterInputTypeLiteral>;
export const ParameterInputType = getNormalEnum(ParameterInputTypeLiteral);

export const ParameterOutputTypeLiteral = z.enum([
  "STRING",
  "NUMBER",
  "DATA_FRAME",
  "DATETIME",
  "BOOLEAN",
  "DYNAMIC",
  "LIST_STRING",
  "LIST_NUMBER",
]);
export type ParameterOutputType = z.infer<typeof ParameterOutputTypeLiteral>;
export const ParameterOutputType = getNormalEnum(ParameterOutputTypeLiteral);

export const KeyValueOption = z.object({
  key: z.string(),
  value: z.string(),
});
export type KeyValueOption = z.infer<typeof KeyValueOption>;

export const DynamicOrStaticList = z.union([
  StringArray,
  NumberArray,
  DynamicValueTypes,
  z.array(KeyValueOption),
]);
export type DynamicOrStaticList = z.infer<typeof DynamicOrStaticList>;

export const BooleanWithNumber = z.object({
  enabled: z.boolean(),
  numberValue: z.number().or(z.null()),
});
export type BooleanWithNumber = z.infer<typeof BooleanWithNumber>;

export const NumberFormattingOptions = z.object({
  format: z.enum(["NUMBER", "PERCENTAGE", "CURRENCY"]).optional(),
  thousands: z.boolean().optional(),
  currencySymbol: z.string().optional(),
});
export type NumberFormattingOptions = z.infer<typeof NumberFormattingOptions>;

export const DropdownOptions = z.object({
  valueOptions: DynamicOrStaticList,
  optional: z.boolean().optional(),
});
export type DropdownOptions = z.infer<typeof DropdownOptions>;

export const TextOptions = z.object({
  multiline: z.boolean(),
});
export type TextOptions = z.infer<typeof TextOptions>;

export const SliderOptions = z.object({
  min: z.number(),
  max: z.number(),
  step: z.number(),
});
export type SliderOptions = z.infer<typeof SliderOptions>;

export const NumericOptions = z.object({
  increment: z.number(),
  numberFormatting: NumberFormattingOptions.optional(),
});
export type NumericOptions = z.infer<typeof NumericOptions>;

export const DatetimeOptions = z.object({
  enableTime: z.boolean(),
  showRelativeDates: z.boolean().optional(),
  dynamicDefault: DynamicValue.optional(),
  useDateRange: z.boolean().optional(),
  enableTimezonePicker: z.boolean().optional(),
});
export type DatetimeOptions = z.infer<typeof DatetimeOptions>;

export const ButtonOptions = z.object({
  intent: z.string(),
  icon: z.string(),
  text: z.string(),
});
export type ButtonOptions = z.infer<typeof ButtonOptions>;

export const CheckboxOptions = z.object({
  style: z.literal("checkbox").or(z.literal("switch")),
  text: z.string(),
});
export type CheckboxOptions = z.infer<typeof CheckboxOptions>;

export const TableOptions = z.object({
  value: DynamicValue.or(z.null()),
});
export type TableOptions = z.infer<typeof TableOptions>;

// Note: option types must all be distinct or it can lead to typeguard issues
export const MultiSelectOptions = z.object({
  multiValueOptions: DynamicOrStaticList,
});
export type MultiSelectOptions = z.infer<typeof MultiSelectOptions>;

export const FileUploadOptions = z.object({
  uploadType: z.literal("csv").or(z.literal("excel")).or(z.literal("binary")),
});
export type FileUploadOptions = z.infer<typeof FileUploadOptions>;

export const ParameterOptions = z.union([
  TextOptions,
  DropdownOptions,
  SliderOptions,
  NumericOptions,
  DatetimeOptions,
  ButtonOptions,
  CheckboxOptions,
  TableOptions,
  MultiSelectOptions,
  FileUploadOptions,
]);
export type ParameterOptions = z.infer<typeof ParameterOptions>;

export const isDynamicDataType = (
  optionsWithDefaults: ParameterOptions,
): boolean => {
  return (
    (zGuard(DropdownOptions, optionsWithDefaults) &&
      zGuard(DynamicValueTypes, optionsWithDefaults.valueOptions)) ||
    (zGuard(MultiSelectOptions, optionsWithDefaults) &&
      zGuard(DynamicValueTypes, optionsWithDefaults.multiValueOptions)) ||
    (zGuard(TableOptions, optionsWithDefaults) &&
      zGuard(DynamicValue, optionsWithDefaults.value))
  );
};

export const ParameterDisplayOptionTypeLiteral = z.enum([
  "STRING",
  "INT",
  "BOOLEAN_WITH_INT",
  "FLOAT",
  "LIST",
  "BOOLEAN",
  "STRING_SELECT",
  "DATAFRAME_SELECT",
  "DYNAMIC_DEFAULT",
  "NUMBER_FORMATTING",
]);
export type ParameterDisplayOptionType = z.infer<
  typeof ParameterDisplayOptionTypeLiteral
>;
export const ParameterDisplayOptionType = getNormalEnum(
  ParameterDisplayOptionTypeLiteral,
);

export const ParameterDisplayOptionSelectValue = z.object({
  value: z.string(),
  label: z.string(),
});
export type ParameterDisplayOptionSelectValue = z.infer<
  typeof ParameterDisplayOptionSelectValue
>;

// Note this is not a schema: it is generic over its type parameter, and
// holds functions that use it, which a schema cannot describe.
export type ParameterDisplayOption<T> = {
  name: string;
  displayName: string;
  type: ParameterDisplayOptionType;
  tooltip?: string;
  allowedValues?: ParameterDisplayOptionSelectValue[];
  labelOnRight?: boolean;
  visibleIf?: (options: T) => boolean;
};

export type InputTypeToOption<T extends ParameterInputType> =
  T extends typeof ParameterInputType.DROPDOWN
    ? DropdownOptions
    : T extends typeof ParameterInputType.NUMERIC_INPUT
      ? NumericOptions
      : T extends typeof ParameterInputType.SLIDER
        ? SliderOptions
        : T extends typeof ParameterInputType.DATE
          ? DatetimeOptions
          : T extends typeof ParameterInputType.BUTTON
            ? ButtonOptions
            : T extends typeof ParameterInputType.CHECKBOX
              ? CheckboxOptions
              : T extends typeof ParameterInputType.TABLE
                ? TableOptions
                : T extends typeof ParameterInputType.MULTISELECT
                  ? MultiSelectOptions
                  : T extends typeof ParameterInputType.FILE_UPLOAD
                    ? FileUploadOptions
                    : T extends typeof ParameterInputType.TEXT_INPUT
                      ? TextOptions
                      : never;

interface ParameterInputTypeMetadata<T extends ParameterOptions> {
  allowedDataTypes: ParameterOutputType[];
  options?: ParameterDisplayOption<T>[];
  defaultOptions: T;
  defaultValue?: string | boolean | number | string[] | number[];
}

export const ParameterInputMetadata: {
  [key in ParameterInputType]: ParameterInputTypeMetadata<
    InputTypeToOption<key>
  >;
} = {
  [ParameterInputType.TEXT_INPUT]: {
    allowedDataTypes: [ParameterOutputType.STRING],
    options: [
      {
        name: "multiline",
        displayName: "Multiline",
        type: ParameterDisplayOptionType.BOOLEAN,
      },
    ],
    defaultOptions: {
      multiline: false,
    },
    defaultValue: "",
  },
  [ParameterInputType.NUMERIC_INPUT]: {
    allowedDataTypes: [ParameterOutputType.NUMBER],
    options: [
      {
        name: "increment",
        displayName: "Increment",
        type: ParameterDisplayOptionType.FLOAT,
      },
      {
        name: "numberFormatting",
        displayName: "Number Formatting",
        type: ParameterDisplayOptionType.NUMBER_FORMATTING,
      },
    ],
    defaultOptions: {
      // Note: Changing these default options will change
      // all existing numeric inputs (which have never had
      // their options changed), not just new ones.
      increment: 1,
      numberFormatting: {
        format: "NUMBER",
        thousands: false,
        currencySymbol: "$",
      },
    },
    defaultValue: 0,
  },
  [ParameterInputType.BUTTON]: {
    allowedDataTypes: [ParameterOutputType.BOOLEAN],
    options: [
      {
        name: "intent",
        displayName: "Style",
        type: ParameterDisplayOptionType.STRING_SELECT,
        allowedValues: [
          {
            label: "Default",
            value: "none",
          },
          {
            label: "Primary",
            value: "primary",
          },
          {
            label: "Success",
            value: "success",
          },
          {
            label: "Warning",
            value: "warning",
          },
          {
            label: "Danger",
            value: "danger",
          },
        ],
      },
      {
        name: "icon",
        displayName: "Icon",
        type: ParameterDisplayOptionType.STRING,
        tooltip:
          "List of available icons here: https://blueprintjs.com/docs/#icons/icons-list",
      },
      {
        name: "text",
        displayName: "Text",
        type: ParameterDisplayOptionType.STRING,
      },
    ],
    defaultOptions: {
      intent: "none",
      icon: "play",
      text: "Run",
    },
  },
  [ParameterInputType.CHECKBOX]: {
    allowedDataTypes: [ParameterOutputType.BOOLEAN],
    options: [
      {
        name: "style",
        displayName: "Style",
        type: ParameterDisplayOptionType.STRING_SELECT,
        allowedValues: [
          {
            label: "Checkbox",
            value: "checkbox",
          },
          {
            label: "Switch",
            value: "switch",
          },
        ],
      },
      {
        name: "text",
        displayName: "Text",
        type: ParameterDisplayOptionType.STRING,
      },
    ],
    defaultOptions: {
      style: "checkbox",
      text: "Checked",
    },
    defaultValue: false,
  },
  [ParameterInputType.DROPDOWN]: {
    allowedDataTypes: [ParameterOutputType.STRING, ParameterOutputType.NUMBER],
    options: [
      {
        name: "valueOptions",
        displayName: "Values",
        type: ParameterDisplayOptionType.LIST,
      },
      {
        name: "optional",
        displayName: "Allow users to clear selection",
        type: ParameterDisplayOptionType.BOOLEAN,
        tooltip: "Allows the dropdown value to be cleared",
      },
    ],
    defaultOptions: {
      valueOptions: { variableName: null },
      optional: false,
    },
  },
  [ParameterInputType.TABLE]: {
    allowedDataTypes: [ParameterOutputType.DATA_FRAME],
    options: [
      {
        name: "value",
        displayName: "Pre-load data",
        type: ParameterDisplayOptionType.DATAFRAME_SELECT,
        tooltip:
          "Start with an empty table or pre-load table values with a dataframe",
      },
    ],
    defaultOptions: {
      value: null,
    },
  },
  [ParameterInputType.SLIDER]: {
    allowedDataTypes: [ParameterOutputType.NUMBER],
    options: [
      {
        name: "min",
        displayName: "Min",
        type: ParameterDisplayOptionType.FLOAT,
      },
      {
        name: "max",
        displayName: "Max",
        type: ParameterDisplayOptionType.FLOAT,
      },
      {
        name: "step",
        displayName: "Step size",
        type: ParameterDisplayOptionType.FLOAT,
      },
    ],
    defaultOptions: {
      min: 0,
      max: 10,
      step: 1,
    },
    defaultValue: 0,
  },
  [ParameterInputType.DATE]: {
    allowedDataTypes: [ParameterOutputType.DATETIME],
    options: [
      {
        name: "dynamicDefault",
        displayName: "Use a variable as the default value",
        type: ParameterDisplayOptionType.DYNAMIC_DEFAULT,
      },
      {
        name: "enableTime",
        displayName: "Time",
        type: ParameterDisplayOptionType.BOOLEAN,
      },
      {
        name: "showRelativeDates",
        displayName: "Relative",
        type: ParameterDisplayOptionType.BOOLEAN,
      },
      {
        name: "testString",
        displayName: "Test string",
        type: ParameterDisplayOptionType.STRING,
      },
    ],
    defaultOptions: {
      enableTime: false,
      showRelativeDates: true,
      dynamicDefault: undefined,
      useDateRange: false,
    },
  },
  [ParameterInputType.MULTISELECT]: {
    allowedDataTypes: [
      ParameterOutputType.LIST_STRING,
      ParameterOutputType.LIST_NUMBER,
    ],
    options: [
      {
        name: "multiValueOptions",
        displayName: "Values",
        type: ParameterDisplayOptionType.LIST,
      },
    ],
    defaultOptions: {
      multiValueOptions: { variableName: null },
    },
    defaultValue: [],
  },
  [ParameterInputType.FILE_UPLOAD]: {
    allowedDataTypes: [ParameterOutputType.DATA_FRAME],
    options: [
      {
        name: "uploadType",
        displayName: "Type",
        type: ParameterDisplayOptionType.STRING_SELECT,
        allowedValues: [
          {
            label: "CSV",
            value: "csv",
          },
          {
            label: "Excel",
            value: "excel",
          },
          {
            label: "File (binary)",
            value: "binary",
          },
        ],
      },
    ],
    defaultOptions: {
      uploadType: "csv",
    },
  },
};

export const typeToHumanString = (
  typeString: string | null | undefined,
): string => {
  if (typeString == null || typeString.length === 0) {
    return "";
  }

  let cleaned = typeString.toLowerCase().replace("_", " ");

  // handle lists separately
  if (cleaned.startsWith("list")) {
    cleaned = cleaned.split(" ").join(" of ") + "s";
  }

  return cleaned.charAt(0).toUpperCase() + cleaned.substring(1);
};
